import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import FeedbackPopup from './FeedbackPopup';

export default function FeedbackTrigger() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Only show on protected routes (after login)
  const protectedRoutes = ['/home', '/grammar', '/sentences', '/dictionary', '/conversations', '/memorize-grammar', '/memorize-sentences', '/memorize-words'];
  const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));

  useEffect(() => {
    // Show button only if user is logged in and on protected routes
    if (currentUser && isProtectedRoute) {
      // Delay showing the button for better UX
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    } else {
      setShowButton(false);
    }
  }, [currentUser, isProtectedRoute]);

  // Don't render anything if conditions aren't met
  if (!currentUser || !isProtectedRoute || !showButton) {
    return null;
  }

  return (
    <>
      {/* Modern Floating Feedback Button */}
      <div className="fixed bottom-6 left-6 z-40">
        <div className="relative">
          {/* Floating button with glassmorphism */}
          <button
            onClick={() => setShowPopup(true)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="group relative w-14 h-14 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300/50 overflow-hidden"
            aria-label="Send Feedback"
            title="Share your thoughts with us"
          >
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 opacity-90"></div>
            
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Icon container */}
            <div className="relative z-10 w-full h-full flex items-center justify-center">
              <div className={`transition-all duration-300 ${isHovered ? 'scale-110 rotate-12' : 'scale-100 rotate-0'}`}>
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>

            {/* Subtle pulse ring animation */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 animate-ping"></div>
          </button>

          {/* Modern hover tooltip only */}
          <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          } pointer-events-none`}>
            <div className="bg-gray-900/90 backdrop-blur-sm text-white text-sm rounded-xl py-2 px-4 whitespace-nowrap shadow-xl border border-gray-700/50">
              <div className="flex items-center space-x-2">
                <span className="text-blue-300">💬</span>
                <span className="font-medium">Share your feedback</span>
              </div>
              {/* Tooltip arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                <div className="border-8 border-transparent border-t-gray-900/90"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Feedback Popup */}
      <FeedbackPopup 
        isVisible={showPopup} 
        onClose={() => setShowPopup(false)} 
      />
    </>
  );
} 